import { useEffect, useState } from 'react';
import { useApp } from '../lib/app-context';

// Waiting, done quietly. Most loads finish in a blink: showing "Loading…" for 100 ms and then removing it is
// what makes an app feel cheap. So nothing is drawn at first; only a wait that lasts shows a skeleton in the
// shape of what is coming (rows, bubbles, a card), and the screen-reader text is always there.

const QUIET_MS = 350;

/** True once `ms` have passed since mount (for showing a wait indicator only when the wait is real). */
export function useDelayed(ms = QUIET_MS): boolean {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setShown(true), ms);
    return () => clearTimeout(timer);
  }, [ms]);
  return shown;
}

/** The start-up screen (same markup and style as index.html, so the hand-over is invisible). */
export function Splash() {
  return (
    <div className="splash" role="status" aria-label="DijitalApp">
      <div className="splash-mark">
        <img src="/brand/icon.svg" alt="" width={76} height={76} />
        <span className="splash-bar" />
      </div>
    </div>
  );
}

type Variant = 'list' | 'card' | 'chat' | 'inline' | 'circles';

/** A wait inside a page: invisible at first, then a skeleton of the coming content. */
export function Loading({ variant = 'list', rows = 4 }: { variant?: Variant; rows?: number }) {
  const { i18n } = useApp();
  const shown = useDelayed();
  return (
    <div className={`loading loading-${variant}${shown ? ' is-shown' : ''}`} role="status" aria-live="polite" aria-busy="true">
      <span className="visually-hidden">{i18n.t('app.loading')}</span>
      {shown && <Shapes variant={variant} rows={rows} />}
    </div>
  );
}

function Shapes({ variant, rows }: { variant: Variant; rows: number }) {
  if (variant === 'inline') return <span className="skel-spinner" aria-hidden />;
  if (variant === 'circles') {
    return (
      <div className="skel-circles" aria-hidden>
        {Array.from({ length: rows }, (_, i) => (
          <span key={i} className="skel skel-circle" />
        ))}
      </div>
    );
  }
  if (variant === 'card') {
    return (
      <div className="skel-card" aria-hidden>
        <span className="skel skel-line" style={{ width: '42%' }} />
        <span className="skel skel-line" style={{ width: '88%' }} />
        <span className="skel skel-line" style={{ width: '76%' }} />
        <span className="skel skel-line" style={{ width: '64%' }} />
      </div>
    );
  }
  if (variant === 'chat') {
    // Bubbles on both sides, of different lengths, like a real conversation.
    const widths = [58, 34, 66, 42, 50, 28];
    return (
      <div className="skel-chat" aria-hidden>
        {widths.slice(0, rows).map((w, i) => (
          <span key={i} className={`skel skel-bubble${i % 3 === 1 ? ' is-mine' : ''}`} style={{ width: `${w}%` }} />
        ))}
      </div>
    );
  }
  const widths = [62, 48, 70, 55, 40, 66, 52];
  return (
    <div className="skel-list" aria-hidden>
      {Array.from({ length: rows }, (_, i) => (
        <span key={i} className="skel-row">
          <span className="skel skel-avatar" />
          <span className="skel-row-text">
            <span className="skel skel-line" style={{ width: `${widths[i % widths.length]}%` }} />
            <span className="skel skel-line skel-line-sm" style={{ width: `${(widths[(i + 3) % widths.length] ?? 50) - 12}%` }} />
          </span>
        </span>
      ))}
    </div>
  );
}
